import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { useUi } from '../../context/UiContext';
import { useI18n } from '../../i18n/useI18n';
import { useDismiss } from '../../hooks/useDismiss';
import { recurrenceLabel } from '../../utils/recurrence';
import { foldText, textDir } from '../../utils/text';
import { startPointerDrag } from '../../dnd/pointerDrag';
import { celebrate } from '../../utils/celebrate';
import { focusField, nextIndex, previousIndex } from '../../utils/focus';
import {
  IconCheck,
  IconClose,
  IconDetails,
  IconGrip,
  IconHighlight,
  IconNoColor,
  IconPlus,
  IconRepeat,
} from '../icons/Icons';
import styles from './board.module.css';

const HIGHLIGHTS = ['gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink'];
const SWATCH_COLUMNS = 5;

export function Bullet({ type, done }) {
  if (done) {
    return (
      <span className={type === 'square' ? `${styles.bulletDone} ${styles.bulletDoneSquare}` : styles.bulletDone}>
        <IconCheck />
      </span>
    );
  }
  if (type === 'square') return <span className={styles.bulletSquare} />;
  if (type === 'plus') {
    return (
      <span className={styles.bulletPlus}>
        <IconPlus />
      </span>
    );
  }
  return <span className={styles.bulletCircle} />;
}

function notesPreview(html) {
  if (!html) return '';
  const probe = document.createElement('div');
  probe.innerHTML = html;
  return (probe.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function matches(text, query) {
  const q = foldText(query.trim());
  if (!q) return false;
  return foldText(text).includes(q);
}

export function TaskColumn({ tasks, scope, loc, placeholder }) {
  const { state, actions } = useApp();
  const { ui } = useUi();
  const composerRef = useRef(null);
  const { showCompleted, bullet, celebrations, showLines } = state.preferences;
  const query = ui.searchOpen ? ui.query : '';

  const commitComposer = (input, { keepFocus = false } = {}) => {
    const lines = input.value
      .split('\n')
      .map((line) => line.replace(/\s+$/g, ''))
      .filter((line) => line.trim().length);
    if (!lines.length) {
      input.value = '';
      input.parentElement?.removeAttribute('dir');
      return;
    }
    input.value = '';
    input.parentElement?.removeAttribute('dir');
    if (lines.length === 1) actions.addTask(loc, lines[0]);
    else actions.addTasks(loc, lines);
    if (keepFocus) requestAnimationFrame(() => input.focus());
  };

  const focusComposer = (event) => {
    if (event.button !== 0) return;
    const row = event.target.closest?.('[data-index]');
    if (row && row.dataset.composer !== 'true') return;
    if (event.target.closest?.('button, a, [role="menu"]')) return;
    if (event.target === composerRef.current) return;
    event.preventDefault();
    composerRef.current?.focus();
  };

  return (
    <div
      className={styles.scroller}
      data-scroller="true"
      data-lines={showLines ? 'true' : 'false'}
      onMouseDown={focusComposer}
    >
      <div className={styles.marker} />
      {tasks.map((task, index) => (
        <TaskRow
          key={task.id}
          task={task}
          index={index}
          tasks={tasks}
          scope={scope}
          loc={loc}
          bullet={bullet}
          celebrations={celebrations}
          showCompleted={showCompleted}
          query={query}
        />
      ))}
      <div className={styles.row} data-index={tasks.length} data-composer="true">
        <span className={`${styles.bulletBtn} ${styles.composerBullet}`} aria-hidden="true">
          <Bullet type={bullet} done={false} />
        </span>
        <input
          ref={composerRef}
          className={styles.input}
          data-focus={`${scope}:composer`}
          placeholder={placeholder}
          aria-label={placeholder}
          onInput={(event) => {
            const dir = textDir(event.currentTarget.value);
            const row = event.currentTarget.parentElement;
            if (!row) return;
            if (dir) row.dir = dir;
            else row.removeAttribute('dir');
          }}
          onBlur={(event) => commitComposer(event.currentTarget)}
          onPaste={(event) => {
            const text = event.clipboardData?.getData('text');
            if (!text || !text.includes('\n')) return;
            event.preventDefault();
            event.currentTarget.value = text;
            commitComposer(event.currentTarget, { keepFocus: true });
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitComposer(event.currentTarget, { keepFocus: true });
              return;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              const prev = previousIndex(tasks, tasks.length, showCompleted);
              if (prev !== null) focusField(scope, prev);
            }
          }}
        />
      </div>
      <div className={styles.pad} />
    </div>
  );
}

function sameDetail(detail, loc, id) {
  if (!detail || detail.id !== id || detail.zone !== loc.zone) return false;
  if (loc.zone === 'day') return detail.date === loc.date;
  return detail.pageId === loc.pageId && detail.listId === loc.listId;
}

function TaskRow({ task, index, tasks, scope, loc, bullet, celebrations, showCompleted, query }) {
  const { t } = useI18n();
  const seriesLabel = task.recurring ? recurrenceLabel(task, t) : '';
  const { actions } = useApp();
  const { ui, openDetail, closeDetail } = useUi();
  const rowRef = useRef(null);
  const inputRef = useRef(null);
  const highlightRef = useRef(null);
  const preview = notesPreview(task.notes);
  const [colorOpen, setColorOpen] = useState(false);
  const open = sameDetail(ui.detail, loc, task.id);
  const q = query.trim();
  const hidden = !showCompleted && task.done;
  const match = q && matches(task.text, q);
  const dim = q && !match;

  useLayoutEffect(() => {
    const field = inputRef.current;
    if (!field) return undefined;
    const measure = () => {
      const row = rowRef.current;
      if (!row) return;
      const probe = document.createElement('span');
      const style = getComputedStyle(field);
      probe.style.cssText = `position:fixed;visibility:hidden;white-space:nowrap;font:${style.font};`;
      probe.textContent = field.value || '';
      document.body.appendChild(probe);
      const textWidth = probe.getBoundingClientRect().width;
      probe.remove();
      if (row.clientWidth < 80) return;
      // Checkbox, row padding, and the hover actions. A title that no longer
      // fits beside those actions gets the wrapped card.
      const available = row.clientWidth - 64 - 96;
      const clipped = textWidth > available;
      const next = Boolean(task.text.trim()) && (clipped || Boolean(preview));
      if (row.getAttribute('data-expand') === String(next)) return;
      if (next) row.setAttribute('data-expand', 'true');
      else row.removeAttribute('data-expand');
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(rowRef.current || field);
    return () => observer.disconnect();
  }, [task.text, preview]);

  const focusAt = (next) => {
    if (next === null) focusField(scope, 'composer');
    else focusField(scope, next);
  };

  const dir = textDir(task.text);

  return (
    <div
      ref={rowRef}
      className={styles.row}
      dir={dir || undefined}
      data-index={index}
      data-match={match ? 'true' : 'false'}
      data-dim={dim ? 'true' : 'false'}
      data-open={open ? 'true' : 'false'}
      data-menu={colorOpen ? 'true' : 'false'}
      data-color={task.color || undefined}
      data-recurring={task.recurring ? 'true' : 'false'}
      hidden={hidden}
      onPointerDown={(event) => {
        if (task.recurring) return;
        startPointerDrag({
          event,
          text: task.text,
          rowEl: rowRef.current,
          source: { ...loc, index, id: task.id },
          onDrop: (source, target) => actions.move(source, target),
        });
      }}
    >
      <span className={styles.grip} aria-hidden="true">
        <IconGrip />
      </span>
      <button
        type="button"
        className={styles.bulletBtn}
        data-no-drag="true"
        aria-label={task.done ? t('task.undone') : t('task.done')}
        aria-pressed={task.done}
        onClick={(event) => {
          if (!task.done && celebrations) {
            const color =
              getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#e10600';
            const openLeft = tasks.filter((item) => !item.done && item.id !== task.id).length;
            celebrate({
              button: event.currentTarget,
              row: rowRef.current,
              color,
              big: openLeft === 0 && tasks.length > 1,
            });
          }
          actions.toggleTask(loc, task.id);
        }}
      >
        <Bullet type={bullet} done={task.done} />
      </button>
      <input
        ref={inputRef}
        className={task.done ? `${styles.input} ${styles.doneInput}` : styles.input}
        data-focus={`${scope}:${index}`}
        value={task.text}
        aria-label={task.text || t('task.label')}
        title={seriesLabel || undefined}
        onChange={(event) => actions.updateTask(loc, task.id, event.target.value)}
        onBlur={(event) => {
          if (document.body.classList.contains('is-dragging')) return;
          if (!event.target.value.trim()) actions.deleteTask(loc, task.id);
        }}
        onKeyDown={(event) => {
          if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown') && !task.recurring) {
            event.preventDefault();
            const direction = event.key === 'ArrowUp' ? -1 : 1;
            const next = index + direction;
            if (next < 0 || next >= tasks.length) return;
            actions.reorder(loc, index, direction);
            focusField(scope, next);
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            focusAt(nextIndex(tasks, index, showCompleted));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = previousIndex(tasks, index, showCompleted);
            if (prev !== null) focusField(scope, prev);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusAt(nextIndex(tasks, index, showCompleted));
          } else if (event.key === 'Backspace' && event.currentTarget.value === '') {
            event.preventDefault();
            const prev = previousIndex(tasks, index, showCompleted);
            actions.deleteTask(loc, task.id);
            focusAt(prev);
          }
        }}
      />
      <div
        className={styles.expand}
        dir={dir || undefined}
        aria-hidden="true"
        onMouseDown={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className={styles.expandTop}>
          {task.color ? <span className={styles.expandSwatch} /> : null}
          <span className={task.done ? `${styles.expandText} ${styles.expandDone}` : styles.expandText}>
            {task.text}
          </span>
        </div>
        {preview ? <span className={styles.expandNotes}>{preview}</span> : null}
      </div>
      {task.recurring && (
        <span className={styles.repeat} title={seriesLabel} aria-hidden="true">
          <IconRepeat />
        </span>
      )}
      <div className={styles.actions} data-no-drag="true">
        <button
          type="button"
          ref={highlightRef}
          className={styles.action}
          aria-label={t('task.highlight')}
          aria-expanded={colorOpen}
          aria-pressed={Boolean(task.color)}
          onClick={() => setColorOpen((value) => !value)}
        >
          <IconHighlight width="15" height="15" />
        </button>
        {colorOpen && (
          <HighlightMenu
            anchorRef={highlightRef}
            color={task.color || ''}
            onClose={() => setColorOpen(false)}
            onPick={(color) => {
              actions.setTaskColor(loc, task.id, color === task.color ? '' : color);
              setColorOpen(false);
            }}
          />
        )}
        <button
          type="button"
          className={styles.action}
          aria-label={t('task.details')}
          aria-pressed={open}
          onClick={() => openDetail({ ...loc, id: task.id })}
        >
          <IconDetails width="15" height="15" />
        </button>
        <button
          type="button"
          className={styles.action}
          aria-label={t('task.delete')}
          onClick={() => {
            if (open) closeDetail();
            actions.deleteTask(loc, task.id);
          }}
        >
          <IconClose width="15" height="15" />
        </button>
      </div>
    </div>
  );
}

function HighlightMenu({ anchorRef, color, onPick, onClose }) {
  const { t } = useI18n();
  const menuRef = useRef(null);
  const [box, setBox] = useState(null);
  useDismiss(true, onClose, [menuRef, anchorRef]);

  useLayoutEffect(() => {
    if (!box) return;
    const current = menuRef.current?.querySelector('[aria-checked="true"]');
    (current || menuRef.current?.querySelector('[data-swatch]'))?.focus();
  }, [box]);

  const onGridKey = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: SWATCH_COLUMNS, ArrowUp: -SWATCH_COLUMNS };
    let step = keys[event.key];
    if (!step) return;
    event.preventDefault();
    if (document.documentElement.dir === 'rtl' && Math.abs(step) === 1) step = -step;
    const swatches = [...menuRef.current.querySelectorAll('[data-swatch]')];
    const index = swatches.indexOf(document.activeElement);
    const next = Math.max(0, Math.min(swatches.length - 1, index + step));
    swatches[next]?.focus();
  };

  useLayoutEffect(() => {
    const place = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 212;
      let left = rect.left;
      if (document.documentElement.dir === 'rtl') left = rect.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      setBox({ top: rect.bottom + 6, left, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef]);

  if (!box) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={styles.highlightMenu}
      role="menu"
      aria-label={t('task.highlight')}
      style={{ top: box.top, left: box.left, width: box.width }}
    >
      <p className={styles.highlightKicker}>{t('task.highlight')}</p>
      <div className={styles.swatchGrid} role="group" onKeyDown={onGridKey}>
        {HIGHLIGHTS.map((item) => {
          const label = t(`task.color.${item}`);
          return (
            <button
              key={item}
              type="button"
              role="menuitemradio"
              aria-checked={color === item}
              aria-label={label}
              title={label}
              className={styles.swatch}
              data-color={item}
              data-swatch=""
              onClick={() => onPick(item)}
            >
              {color === item && <IconCheck width="18" height="18" />}
            </button>
          );
        })}
      </div>
      <div className={styles.highlightDivider} />
      <button
        type="button"
        role="menuitem"
        className={styles.highlightClear}
        disabled={!color}
        onClick={() => onPick('')}
      >
        <IconNoColor width="15" height="15" />
        {t('task.color.none')}
      </button>
    </div>,
    document.body
  );
}
